import { useState, useMemo, useEffect } from 'react';
import { FileText, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { useFileStore } from '../store/fileStore';
import { UploadStatus } from '../types/file.types';

const UPLOADS_PAGE_SIZE = 5;

export const FileUploadProgress = () => {
  const { uploads, clearUpload, clearAllUploads } = useFileStore();
  const [currentPage, setCurrentPage] = useState(1);

  const uploadList = useMemo(() => Array.from(uploads.values()), [uploads]);
  const totalPages = Math.max(1, Math.ceil(uploadList.length / UPLOADS_PAGE_SIZE));
  const pageStart = (currentPage - 1) * UPLOADS_PAGE_SIZE;
  const pageEnd = pageStart + UPLOADS_PAGE_SIZE;
  const paginatedUploads = uploadList.slice(pageStart, pageEnd);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  if (uploadList.length === 0) return null;

  const handleDismissAll = () => {
    clearAllUploads();
    setCurrentPage(1);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium">Uploads</h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismissAll}
          className="h-7 text-muted-foreground hover:text-foreground"
        >
          Dismiss all
        </Button>
      </div>
      <div className="space-y-2">
        {paginatedUploads.map((upload) => (
          <div
            key={upload.fileId}
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3',
              upload.status === UploadStatus.COMPLETED && 'border-green-500/30 bg-green-500/5',
              upload.status === UploadStatus.FAILED && 'border-destructive/30 bg-destructive/5'
            )}
          >
            <div className="flex-shrink-0">
              {upload.status === UploadStatus.UPLOADING && (
                <Spinner size="md" className="h-5 w-5 text-primary" />
              )}
              {upload.status === UploadStatus.COMPLETED && (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
              {upload.status === UploadStatus.FAILED && (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              {upload.status === UploadStatus.PENDING && (
                <FileText className="h-5 w-5 text-muted-foreground" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{upload.fileName}</p>
              {upload.status === UploadStatus.UPLOADING && (
                <Progress value={upload.progress} className="h-1 mt-1" />
              )}
              {upload.status === UploadStatus.FAILED && upload.error && (
                <p className="text-xs text-destructive mt-1">{upload.error}</p>
              )}
            </div>

            <div className="flex-shrink-0">
              {upload.status === UploadStatus.UPLOADING && (
                <span className="text-xs text-muted-foreground">{upload.progress}%</span>
              )}
              {(upload.status === UploadStatus.COMPLETED ||
                upload.status === UploadStatus.FAILED) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => clearUpload(upload.fileId)}
                  className="h-7 px-2"
                >
                  Dismiss
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-2 border-t">
          <p className="text-xs text-muted-foreground">
            {pageStart + 1}&ndash;{Math.min(pageEnd, uploadList.length)} of {uploadList.length}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
